import type { Library, LibraryItem, MediaProgress, Series } from './types';

// Pure helpers that mirror Flutter's AbsApiHelpers mixin and the progress
// helpers of AbsUserProgressApiService (lib/services/abs_api_helpers.dart,
// lib/services/abs_user_progress_api_service.dart:292-377).

/** One shared finished threshold (progress ratio), as in every Flutter code path. */
export const FINISHED_THRESHOLD = 0.98;

/** LibrarySelection sentinel: the connection has libraries but none are enabled. */
export const NO_ENABLED_LIBRARIES = '__no_enabled_libraries__';

const AUDIOBOOK_LIBRARY_TYPES = new Set(['', 'book', 'audiobook', 'music', 'audio', 'artist']);
const AUDIOBOOK_ITEM_TYPES = new Set(['', 'book', 'audiobook', 'series']);

/** `LibraryOption.isAudiobookLibrary`: podcast libraries are excluded everywhere. */
export function isAudiobookLibrary(library: Pick<Library, 'mediaType'>): boolean {
  return AUDIOBOOK_LIBRARY_TYPES.has(String(library.mediaType ?? '').trim().toLowerCase());
}

type MaybeItem = { mediaType?: unknown; media?: unknown };

export function mediaTypeOf(item: MaybeItem): string {
  const media = item.media && typeof item.media === 'object' ? (item.media as { mediaType?: unknown }) : undefined;
  return String(item.mediaType ?? media?.mediaType ?? '').trim().toLowerCase();
}

/** `isAudiobookItem`: null/empty, `book`, `audiobook` or `series`. */
export function isAudiobookItem(item: MaybeItem): boolean {
  return AUDIOBOOK_ITEM_TYPES.has(mediaTypeOf(item));
}

/** `isSeriesEntity`: has a name and (books or no media). */
export function isSeriesEntity(entity: unknown): entity is Series {
  if (!entity || typeof entity !== 'object') return false;
  const e = entity as Record<string, unknown>;
  const hasName = e.name != null;
  const hasBooks = e.books != null || e.numBooks != null;
  const hasMedia = e.media != null;
  return hasName && (hasBooks || !hasMedia);
}

export function isLibraryItem(entity: unknown): entity is LibraryItem {
  return !!entity && typeof entity === 'object' && 'media' in entity && !!(entity as { media?: unknown }).media;
}

/** Lenient boolean parse: bool, number (≠ 0) or `'true'/'1'/'false'/'0'`. */
export function parseLenientBool(value: unknown): boolean | null {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  if (typeof value === 'string') {
    const v = value.trim().toLowerCase();
    if (v === 'true' || v === '1') return true;
    if (v === 'false' || v === '0') return false;
  }
  return null;
}

/**
 * Epoch value in ms. Accepts numbers or numeric strings; `<= 0` → null.
 * Values below 1e11 are treated as seconds (Flutter `_parseEpochDate`).
 */
export function parseEpochMs(value: unknown): number | null {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  if (!Number.isFinite(n) || n <= 0) return null;
  const rounded = Math.round(n);
  return rounded < 100_000_000_000 ? rounded * 1000 : rounded;
}

/** `_progressIsFinished`: the flag when it parses, otherwise `finishedAt > 0`. */
export function progressIsFinished(p: Partial<MediaProgress> | null | undefined): boolean {
  if (!p) return false;
  const flag = parseLenientBool(p.isFinished);
  if (flag != null) return flag;
  return parseEpochMs(p.finishedAt) != null;
}

/** Unique non-empty ids of a progress entry, in Flutter's key order. */
export function progressItemIds(p: Partial<MediaProgress> & Record<string, unknown>): string[] {
  const out: string[] = [];
  for (const key of ['libraryItemId', 'itemId', 'mediaItemId', 'mediaId']) {
    const v = p[key];
    if (typeof v === 'string' && v.trim() && !out.includes(v.trim())) out.push(v.trim());
  }
  return out;
}

export interface TechnicalMetadata {
  /** Bits per second as ABS reports it (format with `raw >= 10000 ? raw / 1000 : raw` kbps). */
  bitrate: number | null;
  codec: string | null;
  /** Container / file type without a leading dot, e.g. `m4b`. */
  container: string | null;
}

type Bag = Record<string, unknown> | undefined;

const asBag = (v: unknown): Bag => (v && typeof v === 'object' ? (v as Record<string, unknown>) : undefined);

function positiveInt(v: unknown): number | null {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  return Number.isFinite(n) && n > 0 ? Math.round(n) : null;
}

function firstText(values: unknown[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

const stripDot = (s: string | null): string | null => (s ? s.replace(/^\./, '') || null : null);

/**
 * Technical tiles (Bitrate / Encoding / File type): first non-null value
 * scanning audio files in order, then media-level, then metadata-level keys
 * (Flutter `Book.fromJson`, lib/models/book.dart:576-640).
 */
export function technicalMetadataOf(item: Pick<LibraryItem, 'media'>): TechnicalMetadata {
  const media = asBag(item.media);
  const metadata = asBag(media?.metadata);
  let bitrate: number | null = null;
  let codec: string | null = null;
  let container: string | null = null;
  const files = Array.isArray(media?.audioFiles) ? (media.audioFiles as unknown[]) : [];
  for (const raw of files) {
    const file = asBag(raw);
    if (!file) continue;
    const fm = asBag(file.metadata);
    bitrate ??= positiveInt(fm?.bitrate ?? fm?.bitRate ?? file.bitrate ?? file.bitRate);
    codec ??= firstText([fm?.codec, fm?.audioCodec, fm?.encoding, file.codec]);
    container ??= firstText([fm?.container, fm?.fileType, fm?.filetype, fm?.format, fm?.ext, file.container, file.ext, file.mimeType]);
    if (bitrate != null && codec != null && container != null) break;
  }
  for (const level of [media, metadata]) {
    bitrate ??= positiveInt(level?.bitrate ?? level?.bitRate);
    codec ??= firstText([level?.codec, level?.audioCodec, level?.encoding]);
    container ??= firstText([level?.container, level?.fileType, level?.filetype, level?.format]);
  }
  return { bitrate, codec, container: stripDot(container) };
}

/** Size in bytes: `media.size`, else the sum of audio file sizes, else the item's root `size`. */
export function itemSizeBytes(item: Pick<LibraryItem, 'media' | 'size'>): number | null {
  const media = asBag(item.media);
  const direct = positiveInt(media?.size);
  if (direct != null) return direct;
  const files = Array.isArray(media?.audioFiles) ? (media.audioFiles as unknown[]) : [];
  let total = 0;
  let found = false;
  for (const raw of files) {
    const file = asBag(raw);
    const size = asBag(file?.metadata)?.size ?? file?.size;
    if (typeof size === 'number' && Number.isFinite(size)) {
      total += size;
      found = true;
    }
  }
  if (found) return total;
  return positiveInt(item.size);
}

/** Runs `fn` over `items` with at most `limit` in flight, keeping result order. */
export async function mapWithConcurrency<T, R>(items: readonly T[], limit: number, fn: (item: T, index: number) => Promise<R>): Promise<R[]> {
  const results = new Array<R>(items.length);
  let next = 0;
  const workers = Array.from({ length: Math.max(1, Math.min(limit, items.length)) }, async () => {
    while (next < items.length) {
      const index = next++;
      results[index] = await fn(items[index] as T, index);
    }
  });
  await Promise.all(workers);
  return results;
}
